import React, { useEffect, useState } from 'react';
import {
    Box,
    useTheme,
    SelectChangeEvent,
    Typography,
    CircularProgress,
} from '@mui/material';
import { CloseButton, ModalContent, StyledModal } from '../TaskDetailEdit/TaskDetailsDropdownModal';
import CommonButton from '../CommonButton';
import { Project } from '../../interfaces/TaskOverview';
import LocalStorageUtil from '../../utils/localStorageUtil';
import { storageKeys } from '../../utils/constants';
import apiService from '../../apiService/ApiService';
import { AxiosResponse, ProjectListingApi, SaveWorkerHoursLogging } from '../../utils/urlConst';
import { Category } from '../../screens/TaskOverview';
import { useAlert } from '../../contexts/AlertContext';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';
import ProjectDropdownLog from './ProjectDropDownLog';
import { ProjectItem, useTimerContext } from '../../contexts/TimerContext';
import useResponsive from '../../hooks/useResponsive';

interface TimerProjectSelectionModalProps {
    handleClose: () => void;
    isVisible: boolean;
    isAnotherProject?: boolean;
    isDropdownDiabled?: boolean;
}

const TimerProjectSelectionModal: React.FC<TimerProjectSelectionModalProps> = ({
    handleClose,
    isVisible,
    isAnotherProject = false,
    isDropdownDiabled = false
}) => {
    const theme = useTheme()
    const [isLoading, setIsLoading] = useState(true);
    const [projectList, setProjectList] = useState<Project[]>([]);
    const [projectSelectedFromDropdown, setProjectSelectedFromDropdown] = useState<ProjectItem | null>(null);
    const { isMobile } = useResponsive();

    const { isTimerOn, setStartTime, setIsTimerOn,
        selectedProjectForLog,
        setSelectedProjectForLog,
        selectedProjectIdForNavigation,
        setIsResetTimer
    } = useTimerContext();
    const { showAlert } = useAlert();
    const navigate = useNavigate();


    // fetch ProjectList api call
    const getProjectList = async () => {
        try {
            // setIsLoading(true)
            let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");
            let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
            let jsondata = JSON.stringify({
                token: userToken,
                user_id: userId,
            });

            apiService.setAuthToken(userToken);
            const responseJson: AxiosResponse | null = await apiService.post(ProjectListingApi, jsondata);
            if (responseJson?.hasOwnProperty('status') && responseJson?.status_code === 1) {
                if (responseJson?.data?.project_listing?.length) {
                    let categoryList = responseJson?.data?.project_listing.map((category: Category) => {
                        return {
                            ...category,
                        };
                    });
                    categoryList.sort((firstCategory: Category, secondCategory: Category) => firstCategory?.project_name.localeCompare(secondCategory?.project_name));

                    setProjectList(categoryList);
                    // setIsLoading(false);
                }
            } else if (responseJson?.hasOwnProperty('status') && (responseJson?.status_code === 2 || responseJson?.status_code === 0)) {
                LocalStorageUtil.clear();
                navigate("/login");
            } else if (responseJson?.hasOwnProperty('status') && responseJson?.status_code === 3) {
                setProjectList([]);
                showAlert(t("taskOverView.no_projects_associated_with_worker"), "info")
                setIsLoading(false);
            } else {
                showAlert(t("common.server_error"), 'error')
                setIsLoading(false);
            }
        } catch (error) {
            showAlert(t("common.server_error"), 'error')
        } finally {
            setIsLoading(false);
        }
    };

    // fetch ProjectList api call
    const saveProjectLogToServer = async (projectId: number, startTime: string | null, endTime: string | null) => {
        try {
            setIsLoading(true)
            let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");
            let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
            let medwerkerId = LocalStorageUtil.getItem(storageKeys.workerId, "");

            let jsondata = JSON.stringify({
                token: userToken,
                user_id: userId,
                medewerker_id: medwerkerId,
                project_id: projectId,
                start_time: startTime,
                end_time: endTime,
            });

            apiService.setAuthToken(userToken);
            const responseJson: AxiosResponse | null = await apiService.post(SaveWorkerHoursLogging, jsondata);
            if (responseJson?.hasOwnProperty('status') && responseJson?.status_code === 1) {
                console.log(" saveProjectLogToServer responseJson", responseJson)
                // showAlert("Log Saved Succesfully", "success")
                showAlert(startTime ? t("timer.projectStarted") : t("timer.projectLogged"), "success")
            } else if (responseJson?.hasOwnProperty('status') && (responseJson?.status_code === 2 || responseJson?.status_code === 0)) {
                LocalStorageUtil.clear();
                navigate("/login");
            } else if (responseJson?.hasOwnProperty('status') && responseJson?.status_code === 3) {
                showAlert(t("taskOverView.no_projects_associated_with_worker"), "info")
                setIsLoading(false);
            } else {
                showAlert(t("common.server_error"), 'error')
                setIsLoading(false);
            }
        } catch (error) {
            showAlert(t("common.server_error"), 'error')
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveStartTime = (selectedProject: ProjectItem) => {
        const projectId = selectedProject.id ?? 0
        const now = new Date()
        const startTime = `${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}`
        console.log("startTime", startTime)
        saveProjectLogToServer(projectId, startTime, null)
            .finally(() => {
                setProjectSelectedFromDropdown(null);
                handleClose();
            })
    }
    const handleSaveEndTime = () => {
        const projectId = selectedProjectForLog?.id ?? 0
        const now = new Date()
        const endTime = `${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}`
        console.log("endTime", endTime)
        saveProjectLogToServer(projectId, null, endTime)
            .finally(() => {
                setSelectedProjectForLog({
                    id: null,
                    name: null,
                })
                setProjectSelectedFromDropdown(null)
                handleClose();
            }
            )
    }

    const handleTimer = () => {
        if (isTimerOn) {
            setIsTimerOn(false)
            setStartTime(0)
            handleSaveEndTime()

        } else {
            const recentProject = getRecentSelectedProject()
            if (recentProject) {
                handleSaveStartTime(recentProject)
                setSelectedProjectForLog(recentProject);
            }
            setIsTimerOn(true);
        }
    };


    const getRecentSelectedProject = () => {
        var selectedProject: ProjectItem | null = null
        if (projectSelectedFromDropdown) {
            selectedProject = projectSelectedFromDropdown;
        } else if (selectedProjectForLog.id) {
            selectedProject = selectedProjectForLog
        } else if (selectedProjectIdForNavigation) {
            const item = projectList.filter((item) => item.id === selectedProjectIdForNavigation)
            if (item.length) {
                const selectedItem = item[0]
                selectedProject = { id: selectedItem.id, name: selectedItem.project_name };
            }
        }
        return selectedProject;
    }


    // dropdown selection
    const handleProjectChange = async (event: SelectChangeEvent<string>) => {
        const selectedProject = JSON.parse(event.target.value);
        setProjectSelectedFromDropdown(selectedProject)
    };


    const switchToNewProject = () => {
        //Need to handle API for end current timer and start new timer
        const projectId = selectedProjectForLog?.id ?? 0
        const now = new Date()
        const endTime = `${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}`
        saveProjectLogToServer(projectId, null, endTime)
            .finally(() => {
                const item = projectList.filter((item) => item.id === selectedProjectIdForNavigation)
                if (item.length) {
                    const selectedItem = item[0]
                    const projectId = selectedItem.id ?? 0
                    const now = new Date()
                    const startTime = `${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}`
                    saveProjectLogToServer(projectId, startTime, null)
                        .finally(() => {
                            handleSwitching(selectedItem);
                        })
                }

            })
    }

    const handleSwitching = (selectedItem: Project) => {
        setSelectedProjectForLog({ id: selectedItem.id, name: selectedItem.project_name })
        setIsResetTimer(true);
        handleClose();
    }

    const closeAction = () => {
        setProjectSelectedFromDropdown(null);
        handleClose()
    }
    const getButtonStatus = () => {
        return (selectedProjectForLog?.id === null &&
            selectedProjectIdForNavigation === null
            && projectSelectedFromDropdown === null) || isLoading
    }
    useEffect(() => {
        // projectList fetch api call
        getProjectList();
    }, []);


    return (
        <StyledModal
            open={isVisible}
            onClose={closeAction}
            aria-labelledby="custom-alert-modal"
            aria-describedby="custom-alert-description"
        >
            <ModalContent maxWidth={isMobile ? 350 : 550}>
                <CloseButton
                    aria-label="close"
                    onClick={closeAction}
                    sx={{ color: "text.secondary" }}
                >
                    <img
                        src={"/assets/ic_alert_close.svg"}
                        alt={`close-icon`}
                        width="16px"
                        height="16px"
                    />
                </CloseButton>

                <Box my={4} sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                    {isAnotherProject ?
                        <Typography color='primary' sx={{
                            fontFamily: "Nunito Sans Regular500",
                            fontSize: "14px",
                            p: 1,
                            mb: 2
                        }}>Wil je beginnen met het volgende project of doorgaan met het huidige project?</Typography> :
                        !isLoading && <Typography color='primary' sx={{
                            fontFamily: "Nunito Sans Regular500",
                            fontSize: "14px",
                            p: 1,
                            mb: 2
                        }}>{isTimerOn ? "Wilt u de timer voor dit project stoppen?" : "Kies je project:"}</Typography>}
                    {!isLoading ? <ProjectDropdownLog
                        projectList={projectList}
                        handleProjectChange={handleProjectChange}
                        isDisabled={isDropdownDiabled}
                        isAnotherProject={isAnotherProject}
                    /> : <CircularProgress />}
                </Box>
                {isAnotherProject ?
                    <Box my={2} sx={{ display: "flex", minWidth: "200px", justifyContent: "center", gap: 2 }} >
                        <CommonButton
                            translationKey={"common.label_cancel"}
                            onClick={handleClose}
                            fullWidth={false}
                            disabled={isLoading}
                            sx={{
                                fontFamily: "Nunito Sans Regular500",
                                fontWeight: 700,
                                backgroundColor: "error.dark",
                                color: theme.palette.secondary.main,
                                px: 3,
                            }}

                        />
                        <CommonButton
                            translationKey={"timer.continue"}
                            onClick={switchToNewProject}
                            fullWidth={false}
                            disabled={isLoading}
                            sx={{
                                fontFamily: "Nunito Sans Regular500",
                                fontWeight: 700,
                                backgroundColor: "primary.main",
                                color: theme.palette.secondary.main,
                                px: 3,
                            }}

                        />
                    </Box> :
                    <Box my={2} display="flex" justifyContent="center">
                        <CommonButton
                            translationKey={isTimerOn ? "timer.endLog" : "timer.startLog"}
                            onClick={handleTimer}
                            fullWidth={false}
                            disabled={getButtonStatus()}
                            sx={{
                                fontFamily: "Nunito Sans Regular500",
                                fontWeight: 700,
                                backgroundColor: isTimerOn ? "error.dark" : "primary.main",
                                color: theme.palette.secondary.main,
                                "&.Mui-disabled": {
                                    bgcolor: "grey.400",
                                    color: "grey.700",
                                },
                            }}

                        />
                    </Box>}
            </ModalContent>
        </StyledModal>
    );
};

export default TimerProjectSelectionModal;